import React, { useEffect, useMemo, useState } from "react";
import { Global } from "../helpers/Global";
import useAuth from "../hooks/useAuth";
import * as XLSX from "xlsx";

// ✅ Toast global
import { useToast } from "../hooks/useToast";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import { DataGrid } from "@mui/x-data-grid";

// Icons (MUI)
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";

// ===== Leaflet / React-Leaflet =====
import { MapContainer, TileLayer, Marker, LayersControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix icon paths (webpack)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Custom markers (rojo/verde)
const edificioIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const cierreIcon = new L.Icon({
  iconUrl:
    "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export const JornadasGerente = () => {
  const { auth } = useAuth();
  const toast = useToast();

  const [jornadas, setJornadas] = useState([]);
  const [centros, setCentros] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  // Filtros
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");

  // paginación DataGrid
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // ===== Modal edición (MUI Dialog) =====
  const [modalEditar, setModalEditar] = useState(false);
  const [jornadaEditar, setJornadaEditar] = useState(null);

  const [editInicio, setEditInicio] = useState("");
  const [editFin, setEditFin] = useState("");
  const [editMotivo, setEditMotivo] = useState("");
  const [editObs, setEditObs] = useState("");
  const [editError, setEditError] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // ===== Modal mapa (MUI Dialog) =====
  const [mostrarMapa, setMostrarMapa] = useState(false);
  const [mapFocus, setMapFocus] = useState("inicio"); // "inicio" | "cierre"
  const [ubicacionInicio, setUbicacionInicio] = useState(null);
  const [ubicacionCierre, setUbicacionCierre] = useState(null);
  const [ubicacionCentro, setUbicacionCentro] = useState(null);

  // --------------------------
  // Helpers robustos (igual estilo Admin)
  // --------------------------
  const parseDateSafe = (value) => {
    if (!value) return null;
    const normalized =
      typeof value === "string" && value.includes(" ") && !value.includes("T")
        ? value.replace(" ", "T")
        : value;

    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const fmtDate = (value) => {
    const d = parseDateSafe(value);
    if (!d) return "-";
    return d.toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const toDatetimeLocalString = (dateValue) => {
    if (!dateValue) return "";
    const d = parseDateSafe(dateValue);
    if (!d) return "";
    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const min = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const calcularDuracion = (ms) => {
    if (!ms || Number.isNaN(ms)) return "No disponible";
    const horas = Math.floor(ms / 3600000);
    const minutos = Math.floor((ms % 3600000) / 60000);
    return `${horas}h ${minutos}min`;
  };

  // --------------------------
  // Carga inicial
  // --------------------------
  useEffect(() => {
    if (!auth?.id) return;
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.id]);

  const cargarDatos = async () => {
    try {
      const [resUsuarios, resCentros, resJornadas] = await Promise.all([
        fetch(`${Global.url}usuarios`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}centros`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
        fetch(`${Global.url}jornadas/finalizadas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ]);

      const usuariosData = resUsuarios.ok ? await resUsuarios.json() : [];
      const centrosData = resCentros.ok ? await resCentros.json() : [];
      const jornadasData = resJornadas.ok ? await resJornadas.json() : [];

      setUsuarios(usuariosData);
      setCentros(centrosData);

      const centrosById = new Map(centrosData.map((c) => [String(c.id), c]));
      const usuariosById = new Map(usuariosData.map((u) => [String(u.id), u]));

      const enriquecidas = jornadasData.map((j) => {
        const centroObj = centrosById.get(String(j.centro_id));
        const usuarioObj = usuariosById.get(String(j.usuario_id));

        const ini = parseDateSafe(j.inicio)?.getTime() ?? 0;
        const fin = parseDateSafe(j.fin)?.getTime() ?? 0;

        return {
          ...j,
          duracionMs: fin - ini,
          centro: centroObj ? centroObj.nombre : `Centro ${j.centro_id}`,
          usuario_nombre: usuarioObj
            ? `${usuarioObj.nombre} ${usuarioObj.apellidos || ""}`.trim()
            : "-",
        };
      });

      setJornadas(enriquecidas);
    } catch (err) {
      console.error("Error al cargar datos:", err);
      toast.error("Error al cargar los datos iniciales.");
    }
  };

  // --------------------------
  // Solo usuarios a cargo del gerente
  // --------------------------
  const usuariosEquipo = useMemo(() => {
    return (usuarios || [])
      .filter((u) => String(u.gerente_id) === String(auth?.id))
      .map((u) => ({
        id: u.id,
        nombre: `${u.nombre} ${u.apellidos || ""}`.trim(),
      }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [usuarios, auth?.id]);

  const usuarioSeleccionado =
    usuariosEquipo.find((u) => String(u.id) === String(filtroUsuario)) || null;

  // --------------------------
  // Filtros + “solo mi equipo”
  // --------------------------
  const jornadasFiltradas = useMemo(() => {
    const idsEquipo = new Set(usuariosEquipo.map((u) => String(u.id)));

    return jornadas.filter((j) => {
      const perteneceAlEquipo = idsEquipo.has(String(j.usuario_id));
      if (!perteneceAlEquipo) return false;

      const dIni = parseDateSafe(j.inicio);
      const inicioISO = dIni ? dIni.toISOString().split("T")[0] : "";

      const matchUsuario = !filtroUsuario || String(j.usuario_id) === String(filtroUsuario);

      const matchFecha =
        !fechaDesde && !fechaHasta
          ? true
          : fechaDesde && !fechaHasta
          ? inicioISO === fechaDesde
          : fechaDesde && fechaHasta
          ? inicioISO >= fechaDesde && inicioISO <= fechaHasta
          : true;

      return matchUsuario && matchFecha;
    });
  }, [jornadas, usuariosEquipo, filtroUsuario, fechaDesde, fechaHasta]);

  useEffect(() => setPage(0), [filtroUsuario, fechaDesde, fechaHasta, pageSize]);

  const limpiarFiltros = () => {
    setFiltroUsuario("");
    setFechaDesde("");
    setFechaHasta("");
    toast.info("Filtros limpiados.");
  };

  // --------------------------
  // Mapa
  // --------------------------
  const abrirMapa = async (j, focus = "inicio") => {
    setMapFocus(focus);

    const ini = j.latitud && j.longitud ? { lat: Number(j.latitud), lng: Number(j.longitud) } : null;
    const fin =
      j.latitud_cierre && j.longitud_cierre
        ? { lat: Number(j.latitud_cierre), lng: Number(j.longitud_cierre) }
        : null;

    setUbicacionInicio(ini);
    setUbicacionCierre(fin);

    try {
      const res = await fetch(`${Global.url}centros/${j.centro_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();

      if (res.ok && data?.latitud && data?.longitud) {
        setUbicacionCentro({
          lat: parseFloat(data.latitud),
          lng: parseFloat(data.longitud),
        });
      } else {
        setUbicacionCentro(null);
      }
    } catch (err) {
      console.error("Error al obtener centro:", err);
      setUbicacionCentro(null);
    }

    if (ini || fin) setMostrarMapa(true);
    else toast.warning("No hay coordenadas para mostrar.");
  };

  const mapCenter = useMemo(() => {
    const pick =
      mapFocus === "cierre"
        ? ubicacionCierre || ubicacionInicio
        : ubicacionInicio || ubicacionCierre;

    return pick ? [pick.lat, pick.lng] : [0, 0];
  }, [mapFocus, ubicacionInicio, ubicacionCierre]);

  // --------------------------
  // Modal edición: precarga
  // --------------------------
  useEffect(() => {
    if (!modalEditar || !jornadaEditar) return;

    setEditInicio(toDatetimeLocalString(jornadaEditar.inicio));
    setEditFin(toDatetimeLocalString(jornadaEditar.fin));
    setEditMotivo(jornadaEditar.motivo_fin || "");
    setEditObs(jornadaEditar.observaciones_admin || jornadaEditar.comentario_admin || "");
    setEditError("");
  }, [modalEditar, jornadaEditar]);

  const handleGuardarEdicion = async () => {
    if (!jornadaEditar) return;

    if (!editInicio || !editFin) {
      setEditError("Debes indicar inicio y fin.");
      return;
    }
    if (!editObs.trim()) {
      setEditError("Debes introducir una observación.");
      return;
    }

    const dInicio = new Date(editInicio);
    const dFin = new Date(editFin);
    if (Number.isNaN(dInicio.getTime()) || Number.isNaN(dFin.getTime()) || dFin < dInicio) {
      setEditError("La fecha de fin debe ser posterior al inicio.");
      return;
    }

    setSavingEdit(true);
    try {
      const res = await fetch(`${Global.url}jornadas/modificar/${jornadaEditar.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          usuario_id: jornadaEditar.usuario_id,
          centro_id: jornadaEditar.centro_id,
          inicio: editInicio,
          fin: editFin,
          motivo_fin: editMotivo,
          observaciones_admin: editObs,
        }),
      });

      if (!res.ok) {
        toast.error("Error al modificar la jornada.");
        return;
      }

      toast.success("Jornada modificada correctamente.");
      setModalEditar(false);
      setJornadaEditar(null);

      await cargarDatos();
    } catch (e) {
      console.error(e);
      toast.error("Error al conectar con el servidor.");
    } finally {
      setSavingEdit(false);
    }
  };

  // --------------------------
  // Export CSV / Excel (todas y filtradas)
  // --------------------------
  const exportarCSVReducido = (datos, nombreArchivo = "jornadas.csv") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const encabezados = [
      "Usuario",
      "Centro",
      "Inicio",
      "Fin",
      "Duración",
      "Distancia (Inicio m)",
      "En Rango (Inicio)",
      "Distancia (Cierre m)",
      "En Rango (Cierre)",
      "Observaciones",
      "Motivo Fin",
    ];

    const csvRows = [
      encabezados.join(","),
      ...datos.map((j) => {
        const fila = [
          j.usuario_nombre ?? "",
          j.centro ?? "",
          j.inicio ? fmtDate(j.inicio) : "-",
          j.fin ? fmtDate(j.fin) : "-",
          calcularDuracion(j.duracionMs),
          j.distancia_m ?? "",
          j.en_rango ? "En rango" : "Fuera de rango",
          j.distancia_m_cierre ?? "",
          j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
          j.observaciones_admin ?? "-",
          j.motivo_fin ?? "-",
        ];
        return fila.map((val) => `"${(val ?? "").toString().replace(/"/g, '""')}"`).join(",");
      }),
    ];

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", nombreArchivo);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`CSV generado: ${nombreArchivo}`);
  };

  const exportarExcel = (datos, nombreArchivo = "jornadas.xlsx") => {
    if (!Array.isArray(datos) || datos.length === 0) {
      toast.warning("No hay datos para exportar.");
      return;
    }

    const datosFormatoExcel = datos.map((j) => ({
      Usuario: j.usuario_nombre ?? "",
      Centro: j.centro ?? "",
      Inicio: j.inicio ? fmtDate(j.inicio) : "-",
      Fin: j.fin ? fmtDate(j.fin) : "-",
      Duración: calcularDuracion(j.duracionMs),
      "Distancia (Inicio m)": j.distancia_m ?? "",
      "En Rango (Inicio)": j.en_rango ? "En rango" : "Fuera de rango",
      "Distancia (Cierre m)": j.distancia_m_cierre ?? "",
      "En Rango (Cierre)":
        j.en_rango_cierre == null ? "" : j.en_rango_cierre ? "En rango" : "Fuera de rango",
      Observaciones: j.observaciones_admin ?? "-",
      "Motivo Fin": j.motivo_fin ?? "-",
    }));

    const hoja = XLSX.utils.json_to_sheet(datosFormatoExcel);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Jornadas");
    XLSX.writeFile(libro, nombreArchivo);

    toast.success(`Excel generado: ${nombreArchivo}`);
  };

  // --------------------------
  // DataGrid rows + columns
  // --------------------------
  const rows = useMemo(
    () =>
      jornadasFiltradas.map((j) => ({
        ...j,
        duracion_txt: calcularDuracion(j.duracionMs),
      })),
    [jornadasFiltradas]
  );

  const columns = useMemo(
    () => [
      { field: "usuario_nombre", headerName: "Usuario", flex: 1, minWidth: 220 },
      { field: "centro", headerName: "Centro", flex: 1, minWidth: 220 },

      { field: "inicio", headerName: "Inicio", minWidth: 170, renderCell: (p) => fmtDate(p.row.inicio) },
      { field: "fin", headerName: "Fin", minWidth: 170, renderCell: (p) => fmtDate(p.row.fin) },

      { field: "duracion_txt", headerName: "Duración", minWidth: 120 },

      {
        field: "distancias",
        headerName: "Distancias",
        minWidth: 320,
        sortable: false,
        renderCell: (params) => {
          const j = params.row;
          const inicioOk = j.en_rango === true;
          const cierreOk = j.en_rango_cierre === true;
          const cierreUnknown = j.en_rango_cierre == null;

          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%", overflow: "hidden" }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "nowrap",
                  whiteSpace: "nowrap",
                  overflowX: "auto",
                  overflowY: "hidden",
                  width: "100%",
                  pb: 0.25,
                  "&::-webkit-scrollbar": { height: 6 },
                }}
              >
                {j.distancia_m != null ? (
                  <Chip
                    icon={<MapIcon />}
                    label={`Inicio: ${j.distancia_m} m`}
                    size="small"
                    color={inicioOk ? "success" : "error"}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(j, "inicio")}
                  />
                ) : (
                  <Chip label="Inicio: -" size="small" variant="filled" />
                )}

                {j.distancia_m_cierre != null ? (
                  <Chip
                    icon={<MapIcon />}
                    label={`Cierre: ${j.distancia_m_cierre} m`}
                    size="small"
                    color={cierreUnknown ? "default" : cierreOk ? "success" : "error"}
                    variant="filled"
                    clickable
                    onClick={() => abrirMapa(j, "cierre")}
                  />
                ) : (
                  <Chip label="Cierre: -" size="small" variant="filled" />
                )}
              </Stack>
            </Box>
          );
        },
      },

      { field: "observaciones_admin", headerName: "Observaciones", minWidth: 260, flex: 1 },
      { field: "motivo_fin", headerName: "Motivo fin", minWidth: 200, flex: 1 },

      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 160,
        sortable: false,
        renderCell: (params) => {
          const j = params.row;
          return (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => {
                  setJornadaEditar(j);
                  setModalEditar(true);
                }}
              >
                Editar
              </Button>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usuariosEquipo] // (solo para evitar recreación excesiva; no depende realmente)
  );

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Jornadas Finalizadas de mi equipo
        </Typography>

        {/* Filtros */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "2fr 1fr 1fr auto",
              },
              alignItems: "end",
            }}
          >
            <Autocomplete
              size="small"
              options={usuariosEquipo}
              getOptionLabel={(u) => u.nombre}
              isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
              value={usuarioSeleccionado}
              onChange={(_, newValue) => setFiltroUsuario(newValue ? newValue.id : "")}
              renderInput={(params) => (
                <TextField {...params} label="Usuario (mi equipo)" placeholder="Escribe para buscar…" />
              )}
              clearOnEscape
            />

            <TextField
              label="Desde"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
            />

            <TextField
              label="Hasta"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
            />

            <Button variant="contained" onClick={limpiarFiltros} sx={{ height: 40 }}>
              Limpiar
            </Button>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 640, p: 1 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            pagination
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(m) => {
              setPage(m.page);
              setPageSize(m.pageSize);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid rgba(0,0,0,0.08)",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              },
            }}
          />
        </Paper>

        {/* Exportaciones + refrescar */}
        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} useFlexGap flexWrap="wrap">
            <Button startIcon={<DownloadIcon />} onClick={() => exportarCSVReducido(jornadasFiltradas, "jornadas_filtradas.csv")}>
              CSV Filtradas
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={() => exportarCSVReducido(jornadas, "todas_jornadas.csv")}>
              CSV Todas
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={() => exportarExcel(jornadasFiltradas, "jornadas_filtradas.xlsx")}>
              Excel Filtradas
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={() => exportarExcel(jornadas, "todas_jornadas.xlsx")}>
              Excel Todas
            </Button>

            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" onClick={cargarDatos}>
              Refrescar
            </Button>
          </Stack>
        </Paper>

        {/* MODAL EDICIÓN */}
        <Dialog
          open={modalEditar}
          onClose={() => {
            if (savingEdit) return;
            setModalEditar(false);
            setJornadaEditar(null);
          }}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>Modificar jornada</DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            <Stack spacing={2}>
              <TextField
                label="Inicio"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editInicio}
                onChange={(e) => setEditInicio(e.target.value)}
              />

              <TextField
                label="Fin"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={editFin}
                onChange={(e) => setEditFin(e.target.value)}
              />

              <TextField
                label="Motivo fin"
                value={editMotivo}
                onChange={(e) => setEditMotivo(e.target.value)}
                placeholder="Motivo de la modificación"
              />

              <TextField
                label="Observaciones"
                value={editObs}
                onChange={(e) => setEditObs(e.target.value)}
                placeholder="Observaciones administrativas"
                multiline
                minRows={3}
              />

              {editError && <Typography color="error">{editError}</Typography>}
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setModalEditar(false);
                setJornadaEditar(null);
              }}
              disabled={savingEdit}
            >
              Cancelar
            </Button>
            <Button onClick={handleGuardarEdicion} disabled={savingEdit}>
              {savingEdit ? "Guardando..." : "Guardar"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* MODAL MAPA */}
        <Dialog open={mostrarMapa} onClose={() => setMostrarMapa(false)} fullWidth maxWidth="md">
          <DialogTitle>Ubicación de la jornada</DialogTitle>
          <Divider />
          <DialogContent sx={{ p: 2 }}>
            {ubicacionInicio || ubicacionCierre ? (
              <Box sx={{ width: "100%", height: 460 }}>
                <MapContainer center={mapCenter} zoom={15} style={{ height: "100%", width: "100%" }}>
                  <LayersControl position="topright">
                    <LayersControl.BaseLayer checked name="Satélite + Callejero">
                      <TileLayer
                        url={`https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}.jpg?key=${process.env.REACT_APP_MAPTILER_KEY}&language=es`}
                        attribution='&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a>'
                      />
                    </LayersControl.BaseLayer>
                  </LayersControl>

                  {ubicacionInicio && (
                    <Marker position={[ubicacionInicio.lat, ubicacionInicio.lng]}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        📍 Jornada (inicio)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCierre && (
                    <Marker position={[ubicacionCierre.lat, ubicacionCierre.lng]} icon={cierreIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        ✅ Jornada (cierre)
                      </Tooltip>
                    </Marker>
                  )}

                  {ubicacionCentro && (
                    <Marker position={[ubicacionCentro.lat, ubicacionCentro.lng]} icon={edificioIcon}>
                      <Tooltip direction="top" offset={[0, -10]}>
                        🏢 Centro de trabajo
                      </Tooltip>
                    </Marker>
                  )}
                </MapContainer>
              </Box>
            ) : (
              <Typography>No hay coordenadas para mostrar.</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant="outlined" onClick={() => setMostrarMapa(false)}>
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
